import { create } from 'zustand'
import { authStorageKey, normalizeStoredRole, roleStorageKey, type UserRole } from '../../../routes/auth'
import { clearAuthTokens, removeAccessToken, removeRefreshTokenCookie } from '../../../shared/utils/tokenStorage'
import { authApi } from '../../../api/auth/auth.api'
import { queryClient } from '../../../lib/queryClient'
import { resetRefreshState, handleTokenRefresh } from '../../../shared/api/interceptors'

export const permissionsStorageKey = 'school-erp-permissions'
export const userStorageKey = 'school-erp-user'

export interface UserIdentity {
  id: string
  name: string
  email: string
  role: UserRole
  permissions: string[]
  organizationId?: string
  branchId?: string
  userType?: string
  rawRole?: string
}

interface AuthState {
  isAuthenticated: boolean
  isInitializing: boolean
  token: string | null
  accessToken: string | null
  resetToken: string | null
  user: UserIdentity | null
  role: UserRole | null
  permissions: string[]
  setToken: (token: string | null) => void
  setAccessToken: (token: string | null) => void
  setResetToken: (token: string | null) => void
  initializeAuth: () => Promise<void>
  login: (role: UserRole, permissions?: string[], userMeta?: Partial<UserIdentity> & { accessToken?: string; token?: string }) => void
  logout: () => void
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
  hasAllPermissions: (permissions: string[]) => boolean
  switchBranchContext: (res: { accessToken: string; user?: any; expiresIn?: number }) => void
}

export const useAuthStore = create<AuthState>((set, get) => {
  const storedPermissions = window.localStorage.getItem(permissionsStorageKey)
  const storedRole = normalizeStoredRole(window.localStorage.getItem(roleStorageKey))
  const storedRawRole = window.localStorage.getItem('school-erp-raw-role') || undefined
  const storedUserRaw = window.localStorage.getItem(userStorageKey)
  
  let role = storedRole
  let initialPermissions: string[] = []
  let initialUser: UserIdentity | null = null
  
  if (storedUserRaw) {
    try {
      initialUser = JSON.parse(storedUserRaw)
      if (initialUser?.permissions && initialUser.permissions.length > 0) {
        initialPermissions = initialUser.permissions
      }
    } catch {
      initialUser = null
    }
  }

  if (initialUser && !initialUser.rawRole && storedRawRole) {
    initialUser.rawRole = storedRawRole
  }

  // Read stored permissions directly from LocalStorage session
  if (storedPermissions && initialPermissions.length === 0) {
    try {
      initialPermissions = JSON.parse(storedPermissions)
    } catch {
      initialPermissions = []
    }
  }

  if (initialPermissions.length === 0 && role) {
    if (role === 'admin' || role === 'superadmin' || role === 'branch_admin' || role === 'organizational_admin') {
      initialPermissions = ['*']
    } else if (role === 'teacher') {
      initialPermissions = ['dashboard:view', 'students:view', 'attendance:view', 'assignments:view', 'lesson-plans.view', 'lesson-plans.create', 'lesson-plans.edit', 'lesson-plans.delete', 'lesson-plans:manage', 'examinations:view', 'leave:apply', 'communication:view', 'library:view', 'profile:view', 'settings:view']
    } else if (role === 'staff') {
      initialPermissions = ['dashboard:view', 'profile:view', 'settings:view', 'staff-directory:view', 'hr.view']
    }
  }

  const initialToken = typeof window !== 'undefined' ? window.localStorage.getItem('access_token') || window.localStorage.getItem('auth_token') || null : null

  return {
    isAuthenticated: window.localStorage.getItem(authStorageKey) === 'true',
    isInitializing: true,
    token: initialToken,
    accessToken: initialToken,
    resetToken: null,
    user: initialUser || (role ? {
      id: `${role}-1`,
      name: role === 'admin' ? 'System Admin' : 'User',
      email: `${role}@school.edu`,
      role: role,
      permissions: initialPermissions,
      rawRole: storedRawRole,
    } : null),
    role,
    permissions: initialPermissions,

    setToken: (token: string | null) => {
      if (typeof window !== 'undefined') {
        if (token) {
          window.localStorage.setItem('access_token', token)
          window.localStorage.setItem('auth_token', token)
        } else {
          window.localStorage.removeItem('access_token')
          window.localStorage.removeItem('auth_token')
        }
      }

      let parsedPermissions: string[] | null = null
      let parsedRole: UserRole | null = null
      let updatedUser: UserIdentity | null = null

      if (token) {
        try {
          const parts = token.split('.')
          if (parts.length === 3) {
            const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
            if (Array.isArray(payload?.permissions) && payload.permissions.length > 0) {
              parsedPermissions = payload.permissions
              if (typeof window !== 'undefined') {
                window.localStorage.setItem(permissionsStorageKey, JSON.stringify(parsedPermissions))
              }
            }
            if (payload?.role) {
              parsedRole = normalizeStoredRole(payload.role)
              if (parsedRole && typeof window !== 'undefined') {
                window.localStorage.setItem(roleStorageKey, parsedRole)
                window.localStorage.setItem('school-erp-raw-role', payload.role)
              }
            }
            const currentUser = get().user
            const rawRole = payload?.role || currentUser?.rawRole || (typeof window !== 'undefined' ? window.localStorage.getItem('school-erp-raw-role') : undefined) || undefined
            updatedUser = {
              id: payload.sub || currentUser?.id || 'user-1',
              name: currentUser?.name || 'User',
              email: currentUser?.email || 'user@school.edu',
              role: parsedRole || currentUser?.role || get().role || 'admin',
              permissions: parsedPermissions || currentUser?.permissions || get().permissions,
              organizationId: payload.orgId || currentUser?.organizationId,
              branchId: Array.isArray(payload.branchIds) ? payload.branchIds[0] : currentUser?.branchId,
              userType: payload.type || currentUser?.userType,
              rawRole,
            }
            if (typeof window !== 'undefined') {
              window.localStorage.setItem(userStorageKey, JSON.stringify(updatedUser))
            }
          }
        } catch {
          // ignore non-JWT or parsing errors
        }
      }

      set((state) => ({
        token,
        accessToken: token,
        isAuthenticated: Boolean(token),
        ...(parsedPermissions ? { permissions: parsedPermissions } : {}),
        ...(parsedRole ? { role: parsedRole } : {}),
        ...(updatedUser ? { user: updatedUser } : {}),
      }))
    },

    setAccessToken: (token: string | null) => {
      get().setToken(token)
    },

    setResetToken: (token: string | null) => {
      set({ resetToken: token })
    },

    initializeAuth: async () => {
      set({ isInitializing: true })
      const existingToken =
        typeof window !== 'undefined'
          ? window.localStorage.getItem('access_token') || window.localStorage.getItem('auth_token')
          : null

      if (existingToken) {
        set({ token: existingToken, accessToken: existingToken, isAuthenticated: true })
      }

      try {
        const freshToken = await handleTokenRefresh()
        if (freshToken) {
          set({ token: freshToken, accessToken: freshToken, isAuthenticated: true, isInitializing: false })
        } else {
          set({ isInitializing: false })
        }
      } catch (err) {
        console.warn('[initializeAuth] Silent refresh on page load failed:', err)
        if (existingToken) {
          set({ token: existingToken, accessToken: existingToken, isAuthenticated: true, isInitializing: false })
        } else {
          set({ isInitializing: false })
        }
      }
    },

    hasPermission: (permission: string) => {
      if (!permission) return true
      const target = permission.trim().toLowerCase()

      // Settings and Profile do not require any explicit permission
      if (
        target === 'settings' ||
        target === 'settings:view' ||
        target === 'settings.view' ||
        target === 'settings.manage' ||
        target === 'adminsettings' ||
        target === 'profile' ||
        target === 'profile:view' ||
        target === 'profile.view' ||
        target === 'teacher-profile:view' ||
        target.includes('settings') ||
        target.includes('profile')
      ) {
        return true
      }

      const activeRole = get().role || normalizeStoredRole(typeof window !== 'undefined' ? window.localStorage.getItem(roleStorageKey) : null)
      const normalizedRole = normalizeStoredRole(activeRole) || activeRole
      const isAdminRole =
        normalizedRole === 'admin' ||
        normalizedRole === 'superadmin' ||
        normalizedRole === 'branch_admin' ||
        normalizedRole === 'branchadmin' ||
        normalizedRole === 'organizational_admin' ||
        normalizedRole === 'org_admin'

      const isLessonPlanTarget = target.startsWith('lesson') || target.includes('lesson-plan') || target.includes('lessonplan')
      const isManageAction = target.includes('create') || target.includes('edit') || target.includes('delete') || target.includes('manage')

      if (isLessonPlanTarget && isManageAction && isAdminRole) {
        const currentPermissions = get().permissions || []
        const hasExplicitManagePermission = currentPermissions.some(
          (p) =>
            p !== '*' &&
            (p.includes('lesson-plans.create') ||
              p.includes('lesson-plans.edit') ||
              p.includes('lesson-plans.delete') ||
              p.includes('lesson-plans.manage') ||
              p.includes('lessonplans:manage') ||
              p.includes('lesson-plans:manage'))
        )
        if (!hasExplicitManagePermission) {
          return false
        }
      }

      const currentPermissions = get().permissions || []

      if (currentPermissions.length > 0) {
        if (currentPermissions.includes('*')) return true
        if (currentPermissions.includes(permission)) return true

        const targetDot = target.replace(/:/g, '.')
        const targetColon = target.replace(/\./g, ':')

        if (currentPermissions.includes(targetDot) || currentPermissions.includes(targetColon)) {
          return true
        }

      // If user has full finance management or admin permissions, grant all fee module actions
      const hasFullFinancePermission = currentPermissions.some(
        (p) =>
          p === 'finance.manage' ||
          p === 'finance:manage' ||
          p === 'finance.all' ||
          p === 'finance:all' ||
          p === 'finance.*' ||
          p === 'finance:*'
      )
      if (
        hasFullFinancePermission &&
        (target.startsWith('finance') ||
          target.startsWith('fee-') ||
          target.startsWith('feehead') ||
          target.startsWith('feestructure') ||
          target.startsWith('feediscount'))
      ) {
        return true
      }

      // Handle wildcard entries in currentPermissions like 'finance:*' or 'communication:*' or 'users.*'
      const hasWildcard = currentPermissions.some((p) => {
        if (p === '*') return true
        if (p.endsWith(':*') || p.endsWith('.*')) {
          const prefix = p.slice(0, -2)
          return target.startsWith(prefix + ':') || target.startsWith(prefix + '.') || target === prefix
        }
        return false
      })
      if (hasWildcard) return true

      // Master alias & sub-permission mapping table
      // Maps fine-grained frontend check targets -> array of backend API permissions that grant it
      const aliasGrantMap: Record<string, string[]> = {
        // --- FEES & FINANCE SUB-PERMISSIONS ---
        'finance': ['finance.view', 'finance:view', 'finance.create', 'finance:create', 'finance.edit', 'finance:edit', 'finance.delete', 'finance:delete', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],
        'finance:view': ['finance.view', 'finance:view', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],
        'finance.view': ['finance.view', 'finance:view', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],
        'finance:overview': ['finance.view', 'finance:view', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],

        // Fee Head
        'finance:fee-head:view': ['finance.view', 'finance:view', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-head:create': ['finance.create', 'finance:create', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-head:edit': ['finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-head:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'fee-head:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'fee-heads:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'feehead:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],

        // Fee Delete & Edit General
        'finance.delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],
        'finance:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],
        'finance.edit': ['finance.edit', 'finance:edit', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],
        'finance:edit': ['finance.edit', 'finance:edit', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],
        'finance.create': ['finance.create', 'finance:create', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],
        'finance:create': ['finance.create', 'finance:create', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],

        // Fee Structure
        'finance:fee-structure:view': ['finance.view', 'finance:view', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-structure:create': ['finance.create', 'finance:create', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-structure:edit': ['finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-structure:submit': ['finance.create', 'finance:create', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-structure:approve': ['finance.approve', 'finance:approve', 'finance.publish', 'finance:publish', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],
        'finance:fee-structure:reject': ['finance.approve', 'finance:approve', 'finance.publish', 'finance:publish', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],
        'finance:fee-structure:publish': ['finance.publish', 'finance:publish', 'finance.approve', 'finance:approve', 'finance.all', 'finance:all', 'finance.manage', 'finance:manage'],
        'finance:fee-structure:withdraw': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-structure:revise': ['finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-structure:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'fee-structure:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'fee-structures:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'feestructure:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-structure:history': ['finance.view', 'finance:view', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],

        // Payments
        'finance:payments:view': ['finance.view', 'finance:view', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:payments:collect': ['finance.create', 'finance:create', 'finance.approve', 'finance:approve', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],

        // Fee Discounts
        'finance:discounts:view': ['finance.view', 'finance:view', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:discounts:apply': ['finance.create', 'finance:create', 'finance.approve', 'finance:approve', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:discounts:create': ['finance.create', 'finance:create', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:discounts:edit': ['finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:discounts:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-discounts:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-discounts:edit': ['finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:fee-discounts:create': ['finance.create', 'finance:create', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'fee-discounts:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'fee-discounts:edit': ['finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'fee-discounts:create': ['finance.create', 'finance:create', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'fee-discount:delete': ['finance.delete', 'finance:delete', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],

        // Reports
        'finance:reports:view': ['finance.view', 'finance:view', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],
        'finance:reports:export': ['finance.export', 'finance:export', 'finance.edit', 'finance:edit', 'finance.all', 'finance:all'],

        // --- USER & ROLE MANAGEMENT ---
        'user-management': ['users.view', 'users:view', 'roles.view', 'roles:view'],
        'user-management:view': ['users.view', 'users:view', 'roles.view', 'roles:view'],
        'user-management.view': ['users.view', 'users:view', 'roles.view', 'roles:view'],
        'user-management:create': ['users.create', 'users:create', 'roles.create', 'roles:create'],
        'user-management.create': ['users.create', 'users:create', 'roles.create', 'roles:create'],
        'user-management:edit': ['users.edit', 'users:edit', 'roles.edit', 'roles:edit'],
        'user-management.edit': ['users.edit', 'users:edit', 'roles.edit', 'roles:edit'],
        'user-management:delete': ['users.delete', 'users:delete', 'roles.delete', 'roles:delete'],
        'user-management.delete': ['users.delete', 'users:delete', 'roles.delete', 'roles:delete'],
        'role-management:view': ['roles.view', 'roles:view'],
        'role-management.view': ['roles.view', 'roles:view'],

        // --- DIRECTORIES ---
        'student-directory:view': ['students.view', 'students:view'],
        'student-directory.view': ['students.view', 'students:view'],
        'student-directory:create': ['students.create', 'students:create', 'students.import'],
        'student-directory.create': ['students.create', 'students:create', 'students.import'],
        'student-directory:edit': ['students.edit', 'students:edit'],
        'student-directory.edit': ['students.edit', 'students:edit'],
        'student-directory:delete': ['students.delete', 'students:delete'],
        'student-directory.delete': ['students.delete', 'students:delete'],
        'staff-directory:view': ['teachers.view', 'teachers:view'],
        'staff-directory.view': ['teachers.view', 'teachers:view'],
        'staff-directory:create': ['teachers.create', 'teachers:create', 'teachers.import'],
        'staff-directory.create': ['teachers.create', 'teachers:create', 'teachers.import'],
        'staff-directory:edit': ['teachers.edit', 'teachers:edit'],
        'staff-directory.edit': ['teachers.edit', 'teachers:edit'],
        'staff-directory:delete': ['teachers.delete', 'teachers:delete'],
        'staff-directory.delete': ['teachers.delete', 'teachers:delete'],

        // --- ACADEMIC & SEAT MANAGEMENT ---
        'seat-management:view': ['seat-management.view', 'seat-management:view', 'admissions.view', 'admissions:view', 'academics.view', 'academics:view'],
        'seat-management.view': ['seat-management.view', 'seat-management:view', 'admissions.view', 'admissions:view', 'academics.view', 'academics:view'],
        'seat-management:create': ['seat-management.create', 'seat-management:create', 'admissions.create', 'admissions:create', 'seat-management.view', 'seat-management:view', 'admissions.view', 'admissions:view', 'academics.view', 'academics:view'],
        'seat-management.create': ['seat-management.create', 'seat-management:create', 'admissions.create', 'admissions:create', 'seat-management.view', 'seat-management:view', 'admissions.view', 'admissions:view', 'academics.view', 'academics:view'],
        'seat-management:edit': ['seat-management.edit', 'seat-management:edit', 'admissions.edit', 'admissions:edit', 'seat-management.view', 'seat-management:view', 'admissions.view', 'admissions:view'],
        'seat-management.edit': ['seat-management.edit', 'seat-management:edit', 'admissions.edit', 'admissions:edit', 'seat-management.view', 'seat-management:view', 'admissions.view', 'admissions:view'],
        'seat-management:delete': ['seat-management.delete', 'seat-management:delete', 'admissions.delete', 'admissions:delete', 'seat-management.view', 'seat-management:view', 'admissions.view', 'admissions:view'],
        'seat-management.delete': ['seat-management.delete', 'seat-management:delete', 'admissions.delete', 'admissions:delete', 'seat-management.view', 'seat-management:view', 'admissions.view', 'admissions:view'],
        'class-management:view': ['grades.view', 'sections.view', 'students.view', 'classes.view', 'classes:view', 'academics.view', 'grades:view', 'class-management.view', 'class-management:view'],
        'class-management.view': ['grades.view', 'sections.view', 'students.view', 'classes.view', 'classes:view', 'academics.view', 'grades:view', 'class-management.view', 'class-management:view'],
        'classes:view': ['grades.view', 'sections.view', 'students.view', 'classes.view', 'classes:view', 'grades:view', 'class-management.view', 'class-management:view'],
        'classes.view': ['grades.view', 'sections.view', 'students.view', 'classes.view', 'classes:view', 'grades:view', 'class-management.view', 'class-management:view'],
        'grades:view': ['grades.view', 'grades:view', 'class-management.view', 'academics.view'],
        'grades.view': ['grades.view', 'grades:view', 'class-management.view', 'academics.view'],
        'academics:view': ['academics.view', 'academics:view', 'academics'],
        'academics.view': ['academics.view', 'academics:view', 'academics'],
        'subjects:view': ['subjects.view', 'subjects:view'],
        'subjects.view': ['subjects.view', 'subjects:view'],

        // --- ADMISSIONS & DOCUMENT VERIFICATION ---
        'admissions:view': ['document-verification.view', 'students.view', 'parents.view'],
        'admissions.view': ['document-verification.view', 'students.view', 'parents.view'],
        'document-verification:view': ['document-verification.view', 'document-verification:view'],
        'document-verification.view': ['document-verification.view', 'document-verification:view'],

        // --- COMMUNICATIONS ---
        'communication:view': ['communications.view', 'communications:view', 'communication.view', 'communication:view'],
        'communication.view': ['communications.view', 'communications:view', 'communication.view', 'communication:view'],
        'communication:create': ['communications.create', 'communications:create'],
        'communication:broadcast': ['communications.create', 'communications:create'],

        // --- HR & SETTINGS ---
        'hr:view': ['teachers.view', 'departments.view', 'designations.view'],
        'hr.view': ['teachers.view', 'departments.view', 'designations.view'],
        'dashboard:view': ['dashboard.view', 'dashboard:view'],
        'dashboard.view': ['dashboard.view', 'dashboard:view'],
        'profile:view': ['profile:view', 'profile.view', 'route:staff:access'],
        'teacher-profile:view': ['teacher-profile:view', 'route:staff:access', 'profile.view'],
        'settings:view': ['settings:view', 'settings.view', 'route:staff:access'],

        // --- LESSON PLANS ---
        'lesson-plans.view': ['lesson-plans.view', 'lesson-plans:view', 'lessonplans.view', 'lessonplans:view', 'lesson-plans:manage', 'lessonplans:manage'],
        'lesson-plans:view': ['lesson-plans.view', 'lesson-plans:view', 'lessonplans.view', 'lessonplans:view', 'lesson-plans:manage', 'lessonplans:manage'],
        'lessonplans:view': ['lesson-plans.view', 'lesson-plans:view', 'lessonplans.view', 'lessonplans:view', 'lesson-plans:manage', 'lessonplans:manage'],
        'lessonplans.view': ['lesson-plans.view', 'lesson-plans:view', 'lessonplans.view', 'lessonplans:view', 'lesson-plans:manage', 'lessonplans:manage'],
        'lesson-plans.create': ['lesson-plans.create', 'lesson-plans:create', 'lessonplans.create', 'lessonplans:create', 'lesson-plans.manage', 'lesson-plans:manage'],
        'lesson-plans:create': ['lesson-plans.create', 'lesson-plans:create', 'lessonplans.create', 'lessonplans:create', 'lesson-plans.manage', 'lesson-plans:manage'],
        'lesson-plans.edit': ['lesson-plans.edit', 'lesson-plans:edit', 'lessonplans.edit', 'lessonplans:edit', 'lesson-plans.manage', 'lesson-plans:manage'],
        'lesson-plans:edit': ['lesson-plans.edit', 'lesson-plans:edit', 'lessonplans.edit', 'lessonplans:edit', 'lesson-plans.manage', 'lesson-plans:manage'],
        'lesson-plans.delete': ['lesson-plans.delete', 'lesson-plans:delete', 'lessonplans.delete', 'lessonplans:delete', 'lesson-plans.manage', 'lesson-plans:manage'],
        'lesson-plans:delete': ['lesson-plans.delete', 'lesson-plans:delete', 'lessonplans.delete', 'lessonplans:delete', 'lesson-plans.manage', 'lesson-plans:manage'],
        'lessonplans:manage': ['lesson-plans.create', 'lesson-plans.edit', 'lesson-plans.delete', 'lesson-plans:manage', 'lessonplans:manage'],
        'lesson-plans:manage': ['lesson-plans.create', 'lesson-plans.edit', 'lesson-plans.delete', 'lesson-plans:manage', 'lessonplans:manage'],
      }

      const acceptableGrants = aliasGrantMap[target] || aliasGrantMap[targetDot] || aliasGrantMap[targetColon]
      if (acceptableGrants && acceptableGrants.some((grant) => currentPermissions.includes(grant))) {
        return true
      }

      // If checking general module-level access without sub-action (e.g. 'communication' or 'users' or 'finance')
      const isPlainModuleName = !target.includes('.') && !target.includes(':')
      if (isPlainModuleName) {
        const aliasedModules: Record<string, string[]> = {
          'user-management': ['users', 'roles', 'departments', 'designations'],
          'student-directory': ['students'],
          'staff-directory': ['teachers'],
          'seat-management': ['seat-management', 'admissions'],
          'class-management': ['grades', 'sections', 'students', 'classes'],
          'classes': ['grades', 'sections', 'students', 'classes'],
          'academics': ['academics', 'grades', 'sections'],
          'subjects': ['subjects'],
          'admissions': ['document-verification', 'students', 'parents'],
          'communication': ['communications', 'communication'],
          'hr': ['teachers', 'departments', 'designations'],
          'finance': ['finance'],
        }

        const checkModules = [target, ...(aliasedModules[target] || [])]
        const matched = currentPermissions.some((p) => {
          const pMod = p.split(/[\.:]/)[0]
          return checkModules.includes(pMod)
        })
        if (matched) return true
      }

      // If explicit permissions array is populated and target did not match, deny permission
      return false
    }

    // Fallback for unconfigured legacy admin sessions where permissions array is empty []
    const fallbackRole = get().role || normalizeStoredRole(typeof window !== 'undefined' ? window.localStorage.getItem(roleStorageKey) : null)
    const fallbackNormalizedRole = normalizeStoredRole(fallbackRole) || fallbackRole
    const fallbackIsAdmin =
      fallbackNormalizedRole === 'admin' ||
      fallbackNormalizedRole === 'superadmin' ||
      fallbackNormalizedRole === 'branch_admin' ||
      fallbackNormalizedRole === 'branchadmin' ||
      fallbackNormalizedRole === 'organizational_admin' ||
      fallbackNormalizedRole === 'org_admin'
    if (fallbackIsAdmin) return true

    return false
    },

    hasAnyPermission: (permissionsList: string[]) => {
      if (!permissionsList || permissionsList.length === 0) return true
      const hasPerm = get().hasPermission
      return permissionsList.some((p) => hasPerm(p))
    },

    hasAllPermissions: (permissionsList: string[]) => {
      if (!permissionsList || permissionsList.length === 0) return true
      const hasPerm = get().hasPermission
      return permissionsList.every((p) => hasPerm(p))
    },

    login: (role: UserRole, customPermissions?: string[], userMeta?: Partial<UserIdentity> & { accessToken?: string; token?: string }) => {
      const perms = customPermissions && customPermissions.length > 0
        ? customPermissions
        : userMeta?.permissions || []

      const userObj: UserIdentity = {
        id: userMeta?.id || `${role}-${Date.now()}`,
        name: userMeta?.name || 'User',
        email: userMeta?.email || `${role}@school.edu`,
        role,
        permissions: perms,
        organizationId: userMeta?.organizationId,
        branchId: userMeta?.branchId,
        userType: userMeta?.userType,
        rawRole: userMeta?.rawRole,
      }

      const existingToken = get().token || get().accessToken || (typeof window !== 'undefined' ? window.localStorage.getItem('access_token') || window.localStorage.getItem('auth_token') : null)
      const token = userMeta?.token || userMeta?.accessToken || existingToken

      window.localStorage.setItem(authStorageKey, 'true')
      window.localStorage.setItem(roleStorageKey, role)
      if (userMeta?.rawRole) {
        window.localStorage.setItem('school-erp-raw-role', userMeta.rawRole)
      }
      window.localStorage.setItem(permissionsStorageKey, JSON.stringify(perms))
      window.localStorage.setItem(userStorageKey, JSON.stringify(userObj))
      if (token && typeof window !== 'undefined') {
        window.localStorage.setItem('access_token', token)
        window.localStorage.setItem('auth_token', token)
      }

      set({
        isAuthenticated: true,
        token: token,
        accessToken: token,
        user: userObj,
        role,
        permissions: perms
      })
    },

    logout: async () => {
      const hasToken = Boolean(get().token || get().accessToken || (typeof window !== 'undefined' ? localStorage.getItem('access_token') || localStorage.getItem('auth_token') : null))
      if (hasToken) {
        try {
          await authApi.logout().catch(() => {})
        } catch {
          // ignore logout network failure
        }
      }
      // 1. Drain in-flight token refresh queue
      resetRefreshState()

        // 2. Purge all cached React Query data from memory
        queryClient.clear()

        // 3. Token & cookie cleanup
        removeAccessToken()
        removeRefreshTokenCookie()
        clearAuthTokens()

        // 4. Session & localStorage items cleanup
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(authStorageKey)
          window.localStorage.removeItem(roleStorageKey)
          window.localStorage.removeItem(permissionsStorageKey)
          window.localStorage.removeItem(userStorageKey)
          window.localStorage.removeItem('access_token')
          window.localStorage.removeItem('auth_token')
          window.localStorage.removeItem('refresh_token')
          window.localStorage.removeItem('branch_id')
          window.localStorage.removeItem('x_branch_id')
          window.localStorage.removeItem('org_id')
          window.localStorage.removeItem('academic_year_id')
          window.localStorage.removeItem('academic_session_id')
          window.localStorage.removeItem('x_academic_year')
          window.localStorage.removeItem('planStorageKey')
          window.localStorage.removeItem('schoolStorageKey')
          window.localStorage.removeItem('supportTicketStorageKey')
        }

        // 5. Reset Zustand state
        set({ isAuthenticated: false, isInitializing: false, token: null, accessToken: null, resetToken: null, user: null, role: null, permissions: [] })
    },

    switchBranchContext: (res: { accessToken: string; user?: any; expiresIn?: number }) => {
      const token = res.accessToken
      if (!token) return

      let parsedPermissions: string[] = res.user?.permissions || []
      let parsedRole: UserRole = 'branch_admin'
      let updatedUser: UserIdentity | null = null

      try {
        const parts = token.split('.')
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
          if (Array.isArray(payload?.permissions) && payload.permissions.length > 0) {
            parsedPermissions = payload.permissions
          }
          if (payload?.role) {
            const mapped = normalizeStoredRole(payload.role)
            if (mapped) parsedRole = mapped
          }
        }
      } catch {
        // ignore jwt decode errors
      }

      const backendUser = res.user || {}
      const branchId = backendUser.branch?.id || (Array.isArray(backendUser.branchIds) ? backendUser.branchIds[0] : undefined)
      const orgId = backendUser.orgId || backendUser.organization?.id

      updatedUser = {
        id: backendUser.id || get().user?.id || 'user-1',
        name: backendUser.fullName || backendUser.name || get().user?.name || 'Admin',
        email: backendUser.email || get().user?.email || 'admin@school.edu',
        role: parsedRole,
        permissions: parsedPermissions,
        organizationId: orgId || get().user?.organizationId,
        branchId: branchId || get().user?.branchId,
        userType: backendUser.userType || backendUser.type || get().user?.userType,
        rawRole: backendUser.role || backendUser.userType,
      }

      if (typeof window !== 'undefined') {
        window.localStorage.setItem('access_token', token)
        window.localStorage.setItem('auth_token', token)
        window.localStorage.setItem(authStorageKey, 'true')
        window.localStorage.setItem(roleStorageKey, parsedRole)
        window.localStorage.setItem(permissionsStorageKey, JSON.stringify(parsedPermissions))
        window.localStorage.setItem(userStorageKey, JSON.stringify(updatedUser))
        if (branchId) {
          window.localStorage.setItem('branch_id', branchId)
          window.localStorage.setItem('active_branch_id', branchId)
          window.localStorage.setItem('x_branch_id', branchId)
        } else {
          window.localStorage.removeItem('branch_id')
          window.localStorage.removeItem('active_branch_id')
          window.localStorage.removeItem('x_branch_id')
        }
        if (orgId) {
          window.localStorage.setItem('org_id', orgId)
          window.localStorage.setItem('active_org_id', orgId)
        }
      }

      queryClient.clear()

      set({
        isAuthenticated: true,
        token,
        accessToken: token,
        user: updatedUser,
        role: parsedRole,
        permissions: parsedPermissions,
      })
    },
  }
})

